// auditpm-v6
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { effectiveStatus, isActiveQuote, familyKey, revNum } from '../lib/quotationStatus'
import { supabase, AUDIT_TYPES, resolveScope, isBizSAFEType } from '../lib/supabase'
import { DEFAULT_QUESTIONNAIRES } from '../lib/questionnaireDefaults'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { getTemplate, applyTemplate } from './Settings'
import { calcBizSAFEMandays, classifyComplexity, complexityBasisText, resolveCT17Industry } from '../lib/ct17'
import CT17Reference from '../components/CT17Reference'
import { resolveExpiry, expiryTier } from '../lib/expiry'
import { quoteSchemeTitle, quoteCompanyName, APPLYING_LEVELS, isNewApplication, applyingLevel, quoteShowsWshcSubmission } from '../lib/quoteTitle'
import { emailHtml, textEmailHtml } from '../lib/emailHtml'
import { isIAQuote, isOTEQuote, standardsLabel } from '../lib/service'
import { oneLine, isAddressField } from '../lib/oneLine'
import { onTabReturn } from '../lib/onTabReturn'
import ServiceSwitch from '../components/ServiceSwitch'
import SendOTEQuestionnaire from '../components/SendOTEQuestionnaire'
import { iaQuoteRows, iaQuoteEmail, iaQuoteStandards, iaFeeText, iaContractYears, IA_CONTRACT_OPTIONS } from '../lib/iaQuote'
import { oteQuotePreviewRows, oteQuoteEmail, OTE_DEFAULT_FEE, OTE_DEFAULT_VALIDITY } from '../lib/oteQuote'
import { toast, failed } from '../lib/toast'

const FIRM_NAME = () => localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd'
const GST = 0.09

// Human-readable labels for all questionnaire fields
const QUESTION_LABELS = {
  application_type: 'Application type',
  current_level: 'Current bizSAFE level',
  expiry_date: 'Current cert expiry',
  expiry_date_progression: 'Current cert expiry',
  company_name: 'Company full name',
  uen: 'Workplace No. / UEN No.',
  company_address: 'Company address',
  num_locations: 'No. of locations',
  locations_detail: 'Processes/activities at each location',
  site_addresses: 'Site location address(es)',
  ssic_code: 'SSIC Code category',
  audit_scope: 'Audit scope',
  other_industry_desc: 'Industry description (Other)',
  complexity: 'Complexity level',
  business_nature: 'Company details / business nature',
  num_employees: 'No. of employees',
  org_chart: 'Organisation chart / key roles',
  outsourced_processes: 'Outsourced processes',
  outsourced_detail: 'Outsourced functions detail',
  consultancy_provided: 'Consultancy relating to RM provided?',
  consultancy_detail: 'Consultancy details',
  management_system_changes: 'Significant changes to management system?',
  management_system_changes_detail: 'Management system changes detail',
  key_personnel_changes: 'Change of management / key personnel?',
  key_personnel_changes_detail: 'Personnel changes detail',
  others: 'Others (certificates / documents)',
  pic_name: 'PIC full name',
  pic_email: 'PIC email',
  pic_phone: 'PIC contact number',
  preferred_audit_dates: 'Preferred audit dates',
  remarks: 'Remarks / questions',
  shms_scope: 'SHMS audit scope',
  site_address: 'Project / worksite address',
  project_name: 'Project name',
  audit_sequence: 'Initial or subsequent audit',
  commencement_date: 'Date work commenced',
  shms_implemented: 'SHMS fully implemented?',
  previous_nc: 'Non-conformities in last audit?',
  wsho_name: 'WSH Officer name',
  iso_standard: 'ISO standard',
  last_internal_audit: 'Date of last internal audit',
  certification_body: 'Certification body',
  cert_expiry: 'Certification expiry date',
  // Client internal audit forms
  standards: 'Standard(s)',
  ia_standards: 'Standard(s) for the internal audit',
  ia_standards_other: 'Other standard(s)',
  iso_cert_expiry: 'Current certificate expiry',
  scope_of_certification: 'Scope of certification',
  knows_next_external: 'Knows next external audit date?',
  next_external_audit_date: 'Next external audit date',
  upload_name: 'External audit report uploaded',
  // OTE questionnaire
  ote_rows: 'Work activities for overtime exemption',
  annex_a_declared: 'Annex A declaration ticked',
  uen_mismatch: 'UEN differs from the client record',
  activities_later: 'Will send the activity list later',
}

// Ordered list matching exact question order in the form
const BIZSAFE_QUESTIONS_ORDERED = [
  'company_name',
  'uen',
  'company_address',
  'pic_name',
  'pic_email',
  'pic_phone',
  'current_level',
  'expiry_date',
  'ssic_code',
  'audit_scope',
  'business_nature',
  'num_employees',
  'num_locations',
  'site_addresses',
  'outsourced_detail',
  'consultancy_detail',
  'management_system_changes_detail',
  'key_personnel_changes_detail',
  'others',
  'remarks',
]
// These four are optional on submission (Winnie, confirmed) — but must never be silently omitted from
// the view when left blank. Shown as "Nil" instead, so it's explicit that the applicant was asked and
// had nothing to declare, rather than looking like the question was never put to them.
const NIL_IF_EMPTY = ['outsourced_detail', 'consultancy_detail', 'management_system_changes_detail', 'key_personnel_changes_detail']

function QuestionnaireView({ responses }) {
  const r = responses || {}
  if (Object.keys(r).length === 0) return (
    <div style={{ color: 'var(--text3)', fontSize: 13, padding: '8px 0' }}>
      No questionnaire data — this quotation was created before the questionnaire system was set up.
    </div>
  )
  // Build display: ordered keys first, then any extra keys not in the ordered list
  const extraKeys = Object.keys(r).filter(k => !BIZSAFE_QUESTIONS_ORDERED.includes(k) && !k.startsWith('_') && k !== 'upload_path' && !(k === 'standards' && r.ia_standards))
  const allKeys = [...BIZSAFE_QUESTIONS_ORDERED, ...extraKeys]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {allKeys.map((k, i) => {
        const v = r[k]
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) return null  // skip internal objects
        // OTE questionnaire: the activity table (array of rows) and Yes/No flags.
        if (k === 'ote_rows' && Array.isArray(v)) return (
          <div key={k} style={{ fontSize: 13, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--text2)', fontWeight: 500, marginBottom: 4 }}>{QUESTION_LABELS[k]} ({v.length})</div>
            {v.map((row, j) => (
              <div key={j} style={{ padding: '3px 0' }}>
                {j + 1}. <strong>{row.work_activities || '—'}</strong>
                <span style={{ color: 'var(--text2)' }}> · {[row.occupation, row.department, row.no_affected ? `${row.no_affected} affected` : ''].filter(Boolean).join(' · ') || '—'}</span>
              </div>
            ))}
          </div>
        )
        let display = Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '')
        const isEmpty = !display || display === 'undefined' || display === 'null'
        if (isEmpty) {
          if (NIL_IF_EMPTY.includes(k)) display = 'Nil'
          else return null
        }
        const label = QUESTION_LABELS[k] || k.replace(/_/g, ' ')
        return (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 8, fontSize: 13, padding: '7px 0', borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)' }}>
            <div style={{ color: 'var(--text2)', lineHeight: 1.5, fontWeight: 500 }}>{label}</div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {Array.isArray(v)
                ? <ul style={{ margin: 0, paddingLeft: 16 }}>{v.map((item, j) => <li key={j}>{item}</li>)}</ul>
                : display}
            </div>
          </div>
        )
      })}
    </div>
  )
}


function fmt(n) {
  if (!n) return '—'
  return 'S$' + Number(n).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function mdLabel(n) {
  const v = parseFloat(n)
  if (!v) return ''
  return `${v} man day${v === 1 ? '' : 's'}`
}

// Quotation lifecycle status helpers now live in ../lib/quotationStatus (shared with the Dashboard KPIs).
const STATUS_LABEL = { Draft: 'Draft', Sent: 'Awaiting signature', Signed: 'Signed', Superseded: 'Superseded', Expired: 'Expired', Declined: 'Declined / Lost', Withdrawn: 'Withdrawn', Archived: 'Archived', Cancelled: 'Cancelled (after signing)' }
function statusLabel(q) { const s = effectiveStatus(q); return STATUS_LABEL[s] || s }


// Lean amendment history rendered from quotations.audit_trail.amendments[]
function AmendmentHistory({ auditTrail }) {
  const amendments = auditTrail?.amendments || []
  if (amendments.length === 0) return null
  return (
    <div style={{ marginTop: 10, background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8 }}>Amendment history ({amendments.length})</div>
      {amendments.map((a, i) => (
        <div key={i} style={{ fontSize: 13, paddingBottom: 8, marginBottom: 8, borderBottom: i < amendments.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
          <div style={{ color: 'var(--text2)' }}>
            {a.amended_at ? format(parseISO(a.amended_at), 'd MMM yyyy HH:mm') : '—'}{a.by ? ' — ' + a.by : ''}
          </div>
          {a.note && <div style={{ marginTop: 2 }}><strong>Reason:</strong> {a.note}</div>}
          {Array.isArray(a.changes) && a.changes.length > 0 && (
            <div style={{ marginTop: 2, color: 'var(--text2)' }}>
              <strong>Fields changed:</strong> {a.changes.map(c => String(c).split(':')[0]).join(', ')}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function EditResponsesModal({ quote, onClose, onSaved }) {
  const { profile } = useAuth()
  const currentUser = profile?.name || localStorage.getItem('user_name') || 'Staff'
  const EDITABLE_FIELDS = [
    { id: 'application_type', label: 'Application type', type: 'select',
      options: ['New Application', 'Renewal', 'Progression'] },
    { id: 'company_name',  label: 'Company full name',   type: 'text' },
    { id: 'uen',           label: 'UEN No.',             type: 'text' },
    { id: 'company_address', label: 'Company address',   type: 'text' },
    { id: 'pic_name',      label: 'PIC name',            type: 'text' },
    { id: 'pic_email',     label: 'PIC email',           type: 'text' },
    { id: 'pic_phone',     label: 'PIC contact number',  type: 'text' },
    { id: 'current_level', label: 'Current bizSAFE level (if Renewal/Progression)', type: 'select',
      options: ['', 'bizSAFE Level 1', 'bizSAFE Level 2', 'bizSAFE Level 3', 'bizSAFE Level 4', 'bizSAFE STAR'] },
    { id: 'expiry_date',   label: 'Current cert expiry date', type: 'date' },
    { id: 'ssic_code',     label: 'SSIC Code', type: 'select',
      options: ['Construction F41-43', 'Manufacturing C10-32 (excl C301)', 'Marine C301/H52252/H52225',
        'Transport & Storage H49-53 (excl H52252 & H52225)', 'None of the above'] },
    { id: 'audit_scope',   label: 'Audit scope', type: 'select',
      options: ['Construction Worksite', 'Metalworking Industry', 'Shipyard',
        'Oil Refinery or Petrochemical Plant', 'Semiconductor Wafer Fabrication Plant',
        'Chemical Manufacturing Plant', 'Pharmaceutical Plant', 'Bulk Storage Terminal', 'Other Industries'] },
    { id: 'business_nature', label: 'Company details (processes & operations)', type: 'textarea' },
    { id: 'num_employees', label: 'Number of employees', type: 'number' },
    { id: 'num_locations', label: 'Number of locations', type: 'number' },
    { id: 'site_addresses', label: 'Worksite addresses', type: 'textarea' },
    { id: 'outsourced_detail', label: 'Outsourced functions (if any)', type: 'textarea' },
    { id: 'management_system_changes_detail', label: 'Significant changes to management system (if any)', type: 'textarea' },
    { id: 'key_personnel_changes_detail', label: 'Changes to management / key personnel (if any)', type: 'textarea' },
    { id: 'consultancy_detail', label: 'Consultancy firm (if any)', type: 'text' },
    { id: 'remarks',       label: 'Remarks', type: 'textarea' },
  ]

  const [fields, setFields] = React.useState(() => {
    // Migrate legacy split expiry: if only the old progression field has a value, fold it into expiry_date
    const f = { ...(quote.form_responses || {}) }
    if (!f.expiry_date && f.expiry_date_progression) f.expiry_date = f.expiry_date_progression
    return f
  })
  const [saving, setSaving] = React.useState(false)
  const [amendNote, setAmendNote] = React.useState('')
  const initialFieldsRef = React.useRef(null)
  if (initialFieldsRef.current === null) initialFieldsRef.current = fields

  // The office edit screen must mirror the QUESTIONNAIRE the client actually filled in — same questions,
  // same order. Hard-coding a second list here is what let the two drift apart (an empty "Worksite
  // addresses" box the questionnaire no longer asks, PIC details floating to the top, "others" missing).
  const [tplQs, setTplQs] = React.useState(null)
  React.useEffect(() => {
    let alive = true
    ;(async () => {
      const canonical = resolveScope(quote.audit_type)?.name || quote.audit_type
      const { data } = await supabase.from('questionnaire_templates').select('questions').eq('audit_type', canonical).maybeSingle()
      const qs = (data?.questions?.length > 0) ? data.questions : (DEFAULT_QUESTIONNAIRES[canonical] || [])
      if (alive) setTplQs(qs)
    })()
    return () => { alive = false }
  }, [quote.audit_type])

  // Questionnaire order first; the office field definition wins where we have one (it carries the proper
  // dropdowns). A field the questionnaire no longer asks is shown ONLY if this submission actually has a
  // value for it, so an older answer stays correctable without adding empty boxes for retired questions.
  const orderedFields = React.useMemo(() => {
    const byId = new Map(EDITABLE_FIELDS.map(f => [f.id, f]))
    const humanize = k => String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    // Descriptor for a questionnaire question: the office field def wins where we have one (proper dropdowns);
    // otherwise carry the questionnaire's own type — INCLUDING multicheck, so "tick all that apply" renders as
    // checkboxes (matching the client form), never a textarea.
    const descFor = (q) => byId.get(q.id) || {
      id: q.id, label: q.label || humanize(q.id),
      type: q.type === 'multicheck' ? 'multicheck' : (q.type === 'textarea' ? 'textarea' : (q.type === 'select' ? 'select' : (q.type || 'text'))),
      options: q.options || [],
    }
    // Collect every field to show: the questionnaire's questions, plus any legacy office field that this
    // submission actually has a value for (so an older answer stays correctable).
    const collected = new Map()
    if (tplQs) tplQs.forEach(q => { if (q?.id && !collected.has(q.id)) collected.set(q.id, descFor(q)) })
    else EDITABLE_FIELDS.forEach(f => collected.set(f.id, f))
    const initial = initialFieldsRef.current || {}
    EDITABLE_FIELDS.forEach(f => {
      if (collected.has(f.id)) return
      const v = initial[f.id]
      if (v != null && String(v).trim() !== '') collected.set(f.id, f)
    })
    // Order to MATCH the read-only questionnaire view (BIZSAFE_QUESTIONS_ORDERED) so a field sits in the same
    // place whether viewing or editing (e.g. PIC details stay near the top, not dumped at the bottom); any
    // remaining fields keep their collected order at the end.
    const out = [], seen = new Set()
    BIZSAFE_QUESTIONS_ORDERED.forEach(id => { if (collected.has(id)) { out.push(collected.get(id)); seen.add(id) } })
    collected.forEach((desc, id) => { if (!seen.has(id)) { out.push(desc); seen.add(id) } })
    return out
  }, [tplQs]) // eslint-disable-line

  // True once any editable field differs from what was loaded (same set that is on screen, so a question
  // that exists only in the questionnaire still demands an amendment note when it is changed).
  const hasChanges = orderedFields.some(f =>
    String(fields[f.id] ?? '') !== String(initialFieldsRef.current[f.id] ?? ''))
  const needsNote = hasChanges && !amendNote.trim()
  const dirty = hasChanges || amendNote.trim() !== ''
  function requestClose() {
    if (dirty && !window.confirm('You have unsaved changes. Are you sure you want to close without saving?')) return
    onClose()
  }

  function set(k, v) { setFields(f => ({ ...f, [k]: v })) }

  async function save() {
    if (!amendNote.trim()) { toast('Add a note on what you changed', 'warning'); return }
    setSaving(true)

    // Recalculate from the (possibly amended) scope.
    // complexityClass defaults to null so a fixed scope clears any stale Other-Industries result.
    const prevScope = quote.form_responses?.audit_scope ?? quote.audit_scope ?? ''
    const scopeChanged = (fields.audit_scope || '') !== prevScope
    let mandays = quote.mandays
    let calcDetails = quote.calc_details
    let complexityClass = null

    if (quote.audit_type === AUDIT_TYPES.BIZSAFE) {
      // Single source of truth. Fixed scope resolves directly; only "Other Industries" uses complexity.
      let industry
      if (fields.audit_scope === 'Other Industries') {
        const existing = (() => { try { return quote.complexity_classification ? JSON.parse(quote.complexity_classification) : null } catch (e) { return null } })()
        if (existing && existing.override_reason && !scopeChanged) {
          // preserve a manual override unless the scope itself changed
          complexityClass = quote.complexity_classification
          industry = existing.industry || classifyComplexity(fields.ssic_code, fields.business_nature).industry
        } else {
          const classified = classifyComplexity(fields.ssic_code, fields.business_nature)
          industry = classified.industry
          complexityClass = JSON.stringify({ ...classified, determined_by: 'System', determined_at: new Date().toISOString() })
        }
      } else {
        industry = resolveCT17Industry(fields.audit_scope)
        // Fixed scope: complexityClass stays null (stale Other-Industries result cleared).
      }
      const result = calcBizSAFEMandays(industry, fields.num_employees, fields.num_locations || 1)
      mandays = result.totalMandays
      calcDetails = result
    }

    // Build amendment audit trail — compare against the migrated baseline captured on open
    // (not the raw form_responses), so the automatic expiry-field consolidation isn't logged as a change.
    const changes = []
    const orig = initialFieldsRef.current || {}
    orderedFields.forEach(f => {
      const oldVal = String(orig[f.id] || '')
      const newVal = String(fields[f.id] || '')
      if (oldVal !== newVal) changes.push(`${f.label}: "${oldVal}" → "${newVal}"`)
    })

    const amendLog = {
      ...(typeof quote.audit_trail === 'object' ? quote.audit_trail : {}),
      amendments: [
        ...((quote.audit_trail?.amendments) || []),
        { note: amendNote, changes, amended_at: new Date().toISOString(), by: currentUser }
      ]
    }

    // Keep the canonical prospect_name column in sync with the edited company name so it never goes stale
    // (prospect-stage lists/dashboards read this column). Only overwrite when the form actually has a value.
    const syncedProspectName = fields.company_name || fields['Company full name'] || quote.prospect_name || null
    const cleaned = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, isAddressField({ id: k }) && typeof v === 'string' ? oneLine(v).trim() : v]))
    await supabase.from('quotations').update({
      form_responses: cleaned,
      prospect_name: syncedProspectName,
      audit_scope: fields.audit_scope || null,
      mandays, calc_details: calcDetails,
      complexity_classification: complexityClass,
      business_nature: fields.business_nature || '',
      num_employees: parseInt(fields.num_employees) || null,
      num_locations: parseInt(fields.num_locations) || 1,
      audit_trail: amendLog,
    }).eq('id', quote.id)

    onSaved({ ...quote, form_responses: cleaned, prospect_name: syncedProspectName, audit_scope: fields.audit_scope || null,
      mandays, calc_details: calcDetails,
      num_employees: parseInt(fields.num_employees) || null,
      num_locations: parseInt(fields.num_locations) || 1,
      complexity_classification: complexityClass, audit_trail: amendLog })
    setSaving(false)
    onClose()
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit questionnaire responses</h2>
          <button onClick={requestClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
          Correct client-entered information. All changes are logged below and to the audit trail.
        </p>
        <AmendmentHistory auditTrail={quote.audit_trail} />
        <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 4, marginTop: 10 }}>
          {orderedFields.map(f => (
            <div key={f.id} className="field">
              <label style={{ fontSize: 13 }}>{f.label}</label>
              {f.type === 'multicheck' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 2 }}>
                  {(f.options || []).map(opt => {
                    const arr = Array.isArray(fields[f.id]) ? fields[f.id] : (fields[f.id] ? [fields[f.id]] : [])
                    const checked = arr.includes(opt)
                    return (
                      <label key={opt} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checked} style={{ marginTop: 2, width: 'auto', flexShrink: 0 }}
                          onChange={() => set(f.id, checked ? arr.filter(x => x !== opt) : [...arr, opt])} />
                        <span>{opt}</span>
                      </label>
                    )
                  })}
                </div>
              ) : isAddressField(f) ? (
                // Address: one line (line breaks become ", ").
                <input value={oneLine(fields[f.id])} onChange={e => set(f.id, oneLine(e.target.value))} />
              ) : f.type === 'textarea' ? (
                <textarea value={fields[f.id] || ''} onChange={e => set(f.id, e.target.value)} rows={2} />
              ) : f.type === 'select' ? (
                <select value={fields[f.id] || ''} onChange={e => set(f.id, e.target.value)}>
                  {(f.options[0] !== '' ? ['', ...f.options] : f.options).map(o => (
                    <option key={o} value={o}>{o || '— select —'}</option>
                  ))}
                </select>
              ) : (
                <input type={f.type || 'text'} value={fields[f.id] || ''} onChange={e => set(f.id, e.target.value)} />
              )}
            </div>
          ))}
          {/* Catch-all: any captured answer NOT shown above (multi-select "others", or questions from a
              customised questionnaire) — read-only, so the office always sees the COMPLETE submission. */}
          {(() => {
            const shown = new Set(orderedFields.map(f => f.id))
            const skip = new Set(['expiry_date_progression'])
            const humanize = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            const render = v => Array.isArray(v) ? v.join('; ') : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))
            const extra = Object.entries(fields).filter(([k, v]) =>
              !shown.has(k) && !skip.has(k) && v != null && String(v).trim() !== '' && (!Array.isArray(v) || v.length > 0))
            if (!extra.length) return null
            return (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Answers from an earlier version of this questionnaire (read-only)</div>
                {extra.map(([k, v]) => (
                  <div key={k} style={{ fontSize: 13, marginBottom: 6 }}>
                    <div style={{ color: 'var(--text3)' }}>{humanize(k)}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{render(v)}</div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, color: needsNote ? 'var(--danger)' : undefined }}>
            Amendment note * (reason for change){needsNote ? ' — required, you changed a response' : ''}
          </label>
          <input value={amendNote} onChange={e => setAmendNote(e.target.value)}
            placeholder="e.g. Client provided wrong UEN, corrected to 202312345A"
            style={needsNote ? { borderColor: 'var(--danger)', background: '#fdecea', boxShadow: '0 0 0 1px var(--danger)' } : undefined} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="primary" onClick={save} disabled={saving || !amendNote.trim()}>
            {saving ? 'Saving…' : 'Save corrections'}
          </button>
          <button onClick={requestClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function QuoteModal({ quote, client, onClose, onSaved, onUpdate, priorVersions = [] }) {
  const { profile, logAction } = useAuth()
  // Client internal audit quote: no CT 17, no bizSAFE expiry, no timeline — man-days typed by the office.
  const ia = isIAQuote(quote)
  // OTE quote: like Internal Audit, no CT 17 / bizSAFE expiry / timeline; fixed fee pre-filled (owner's quotation).
  const ote = isOTEQuote(quote)
  const other = ia || ote
  const currentUser = profile?.name || localStorage.getItem('user_name') || 'Staff'
  const [editResponses, setEditResponses] = React.useState(false)
  const [showComplexityOverride, setShowComplexityOverride] = React.useState(false)
  const [overrideVal, setOverrideVal] = React.useState('')
  const [overrideReason, setOverrideReason] = React.useState('')
  const [savingOverride, setSavingOverride] = React.useState(false)

  async function saveComplexityOverride() {
    if (!overrideVal || !overrideReason.trim()) { toast('Choose a classification and give a reason', 'warning'); return }
    setSavingOverride(true)
    const old = (() => { try { return typeof quote.complexity_classification === 'string' ? JSON.parse(quote.complexity_classification) : (quote.complexity_classification || {}) } catch (e) { return {} } })()
    const industryMap = { 'High': 'Other - High complexity', 'Medium': 'Other - Medium complexity', 'Low': 'Other - Low complexity', 'Pending Review': 'Other - Pending Review' }
    const newIndustry = industryMap[overrideVal] || old.industry
    const next = { ...old, complexity: overrideVal, industry: newIndustry,
      reason: 'Manually confirmed: ' + overrideReason.trim(),
      determined_by: currentUser, determined_at: new Date().toISOString(), override_reason: overrideReason.trim() }
    const newStr = JSON.stringify(next)

    // Recompute CT17 man-days for the new complexity (table logic unchanged).
    const fr = quote.form_responses || {}
    const recalc = calcBizSAFEMandays(newIndustry, quote.num_employees ?? fr.num_employees, quote.num_locations ?? fr.num_locations ?? 1)
    const newCt = recalc.totalMandays
    // Safety: only auto-update the man-days field if the user hasn't manually changed it
    // away from the previous CT17 baseline. Empty field counts as untouched.
    const mandaysUntouched = mandays === '' || parseFloat(mandays) === parseFloat(ctMandays)

    const dbUpdate = { complexity_classification: newStr, calc_details: recalc }
    if (mandaysUntouched) dbUpdate.mandays = newCt
    const { error } = await supabase.from('quotations').update(dbUpdate).eq('id', quote.id)
    if (error) { failed("Couldn't save the override", error.message); setSavingOverride(false); return }
    if (logAction) await logAction('Complexity overridden', 'quotation', quote.id, quote.quote_ref, {
      old: old.complexity || '—', new: overrideVal, by: currentUser, reason: overrideReason.trim(),
      ct17_recalculated: String(newCt), mandays_field_updated: mandaysUntouched })
    if (mandaysUntouched) setMandays(newCt)           // sync the editable man-days field
    if (onUpdate) onUpdate({ id: quote.id, ...dbUpdate })  // update in place, keep modal open
    setSavingOverride(false); setShowComplexityOverride(false); setOverrideReason('')
  }
  const feeInit = quote.fee_ex_gst || quote.fee || (ote ? String(OTE_DEFAULT_FEE) : '')
  const [fee, setFee] = useState(feeInit)
  const [mandays, setMandays] = useState(quote.mandays || '')
  // Suggested fee — two tiers: (1) client's last signed rate (per half man-day, derived from
  // their most recent Signed quotation), else (2) the standard rate card. Suggestion only —
  // the verifier always confirms/edits. Auto-fills the fee field ONLY when it is empty.
  const [feeSuggestion, setFeeSuggestion] = useState(null)  // { amount, ratePerHalf, source }
  useEffect(() => {
    let cancelled = false
    async function suggest() {
      const md = parseFloat(mandays)
      if (ote || !md || md <= 0) { setFeeSuggestion(null); return }   // OTE: fixed fee, no bizSAFE rate card
      let ratePerHalf = null, source = ''
      if (quote.client_id) {
        const { data: last } = await supabase.from('quotations')
          .select('fee_ex_gst, mandays, quote_ref, signed_date')
          .eq('client_id', quote.client_id).eq('status', 'Signed')
          .not('fee_ex_gst', 'is', null).gt('mandays', 0)
          .neq('id', quote.id)
          .order('signed_date', { ascending: false }).limit(1).maybeSingle()
        if (last && last.fee_ex_gst && last.mandays) {
          ratePerHalf = last.fee_ex_gst / (last.mandays * 2)
          source = `client's last signed rate (${last.quote_ref})`
        }
      }
      if (ratePerHalf == null) {
        const { data: card } = await supabase.from('rate_cards')
          .select('rate_per_half_manday').eq('scheme', 'bizSAFE RM').maybeSingle()
        if (card) { ratePerHalf = parseFloat(card.rate_per_half_manday); source = 'standard rate card' }
      }
      if (cancelled || ratePerHalf == null) return
      const amount = Math.round(ratePerHalf * md * 2 * 100) / 100
      setFeeSuggestion({ amount, ratePerHalf, source })
    }
    suggest()
    return () => { cancelled = true }
  }, [quote.id, quote.client_id, mandays])
  // Pre-fill an EMPTY fee field once the suggestion arrives (never overwrites a typed/saved fee).
  useEffect(() => {
    if (feeSuggestion && (fee === '' || fee == null)) setFee(String(feeSuggestion.amount))
  }, [feeSuggestion])
  const [mandayJustification, setMandayJustification] = useState(quote.manday_justification || '')
  const validityInit = quote.validity_days ?? (ote ? OTE_DEFAULT_VALIDITY : 30)
  const [validity, setValidity] = useState(validityInit)
  const [timeline, setTimeline] = useState(quote.timeline || '~2 months')
  const [remarks, setRemarks] = useState(quote.remarks || '')
  // New bizSAFE application → the level the client is going for (Level 3 unless the office picks otherwise).
  const pickLevel = !ia && isBizSAFEType(quote.audit_type) && isNewApplication(quote)
  const levelInit = applyingLevel(quote)
  const [level, setLevel] = useState(levelInit)
  // Internal audit: contract length (1 audit a year, invoiced yearly). 1 = single audit.
  const yearsInit = iaContractYears(quote)
  const [contractYears, setContractYears] = useState(yearsInit)
  const [saving, setSaving] = useState(false)

  const feeNum = parseFloat(fee) || 0
  const feeInc = Math.round(feeNum * (1 + GST) * 100) / 100
  // CT17 baseline, computed LIVE from the CURRENT complexity + employees + sites so it
  // always reflects a manual override (and self-heals legacy quotes whose stored calc_details
  // pre-dates an override). Falls back to the stored value for fixed-scope/SHMS quotes that
  // carry no "Other Industries" complexity industry. CT17 table logic itself is unchanged.
  const ctMandays = (() => {
    try {
      const cc = typeof quote.complexity_classification === 'string'
        ? JSON.parse(quote.complexity_classification) : quote.complexity_classification
      const ind = cc?.industry
      if (!ind) return quote.calc_details?.totalMandays || quote.calc_details?.mandays
      const fr = quote.form_responses || {}
      const emp = quote.num_employees ?? fr.num_employees
      const sites = quote.num_locations ?? fr.num_locations ?? 1
      return calcBizSAFEMandays(ind, emp, sites).totalMandays
    } catch (e) {
      return quote.calc_details?.totalMandays || quote.calc_details?.mandays
    }
  })()

  // Man-day deviation vs the CT 17 baseline. There is NO second-person approval anywhere:
  //  - below baseline → justification mandatory, but the preparer can still send (recorded to audit trail)
  //  - at/above baseline → send directly, no control
  //  - complexity Pending Review → hard block until confirmed/overridden
  const belowBaseline = ctMandays != null && mandays !== '' && parseFloat(mandays) < parseFloat(ctMandays)
  const complexityPending = (() => { try { const cc = typeof quote.complexity_classification === 'string' ? JSON.parse(quote.complexity_classification) : quote.complexity_classification; return cc?.complexity === 'Pending Review' } catch (e) { return false } })()
  const justificationMissing = belowBaseline && !mandayJustification.trim()
  const canSend = !complexityPending && !justificationMissing && (!ia || parseFloat(mandays) > 0)   // IA: man-days typed in (OTE: fixed fee)

  async function save(action) {
    setSaving(true)
    const updates = {
      fee: feeNum, fee_ex_gst: feeNum, fee_inc_gst: feeInc,
      mandays: parseFloat(mandays) || quote.mandays,
      manday_justification: belowBaseline ? mandayJustification.trim() : null,
      validity_days: parseInt(validity),
      timeline, remarks,
      prepared_by: quote.prepared_by || currentUser,
      // The picked level travels with the questionnaire answers (→ job form_data on signing) and is kept on
      // bizsafe_level so the client's next renewal form pre-fills it.
      ...(pickLevel ? { form_responses: { ...(quote.form_responses || {}), applying_level: level }, bizsafe_level: level } : {}),
      ...(ia ? { ia_contract_years: contractYears } : {}),
    }
    // Retire any legacy approval statuses on touch (no approval workflow exists anymore)
    if (['Pending approval', 'Approved'].includes(quote.status)) updates.status = 'Draft'
    if (action === 'send' || action === 'send-outlook') {
      if (complexityPending) { toast('Confirm the complexity first', 'warning', { detail: "It's still Pending Review" }); setSaving(false); return }
      if (justificationMissing) { toast('Justify the lower man-days', 'warning', { detail: "They're below the CT17 baseline" }); setSaving(false); return }
      updates.signature_token = Math.random().toString(36).slice(2) + Date.now().toString(36)
      updates.status = 'Sent'
      updates.sent_date = new Date().toISOString().split('T')[0]
      // CT17 application acceptance: sending the quotation IS the acceptance point. Stamp who/when on the
      // first send (context — quote ref, audit type, employees/locations, man-days, justification — already
      // lives on the quotation). No extra UI, no separate review form.
      if (!quote.application_accepted_at) {
        updates.application_accepted_by = currentUser
        updates.application_accepted_at = new Date().toISOString()
      }
    }
    const { error } = await supabase.from('quotations').update(updates).eq('id', quote.id)
    if (error) { failed("Couldn't save", error.message); setSaving(false); return }
    if ((action === 'send' || action === 'send-outlook') && !quote.application_accepted_at && logAction) {
      await logAction('Application accepted via quotation', 'quotation', quote.id, quote.quote_ref, {
        by: currentUser, audit_type: quote.audit_type, num_employees: quote.num_employees, num_locations: quote.num_locations,
        mandays: String(mandays || quote.mandays || ''), justification: mandayJustification.trim() || null })
    }
    // Record a below-baseline man-day deviation to the audit trail (who/when/why)
    if ((action === 'send' || action === 'send-outlook') && belowBaseline && logAction) {
      await logAction('Quotation man-days below CT17 baseline', 'quotation', quote.id, quote.quote_ref, {
        quoted_mandays: String(mandays), ct17_baseline: String(ctMandays), justification: mandayJustification.trim(), by: currentUser })
    }
    onSaved({ ...quote, ...updates, _sendMode: action })
    setSaving(false)
  }
  const r = quote.form_responses || {}
  // Only the latest active version (Draft / Awaiting signature) may be edited. History
  // versions (Signed / Superseded / Expired / Declined / Withdrawn) are read-only.
  const editable = effectiveStatus(quote) === 'Draft' || effectiveStatus(quote) === 'Sent'

  // Unsaved-changes detection (compare editable fields to what was loaded)
  const dirty =
    String(fee) !== String(feeInit) ||
    String(mandays) !== String(quote.mandays ?? '') ||
    (mandayJustification || '') !== (quote.manday_justification || '') ||
    String(validity) !== String(validityInit) ||
    (timeline || '') !== (quote.timeline || '~2 months') ||
    (remarks || '') !== (quote.remarks || '') ||
    level !== levelInit || contractYears !== yearsInit
  function requestClose() {
    if (dirty && !window.confirm('You have unsaved changes. Are you sure you want to close without saving?')) return
    onClose()
  }

  return (
    <>
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Quotation — {quote.quote_ref}</h2>
          <button onClick={requestClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>

        {/* Client details pulled from questionnaire */}
        <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>{quoteCompanyName(quote, client) || '—'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '4px 16px', color: 'var(--text2)' }}>
            <div>Contact: {r.pic_name || client?.contact_name || '—'}</div>
            <div>Email: {r.pic_email || client?.contact_email || '—'}</div>
            <div>Phone: {r.pic_phone || client?.contact_phone || '—'}</div>
            {ia ? <div>Standard(s): {standardsLabel(iaQuoteStandards(quote)) || '—'}</div>
              : ote ? <div>UEN: {r.uen || '—'}</div>
              : <div>Employees: {r.num_employees || '—'}</div>}
            <div style={{ gridColumn: '1 / -1' }}>Business nature: {r.business_nature || client?.notes || client?.industry || '—'}</div>
          </div>
        </div>

        {/* Returning client changes notice */}
        {quote.client_changes && Object.keys(quote.client_changes).length > 0 && (
          <div style={{ background: '#fff8e6', border: '1px solid #f0c040', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8, color: '#7a5a00' }}>
              ⚠️ Returning client — details have changed since last time
            </div>
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(quote.client_changes).map(([field, change]) => (
                <div key={field} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 8 }}>
                  <span style={{ color: '#7a5a00', fontWeight: 500, textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}</span>
                  <span style={{ color: '#999', textDecoration: 'line-through' }}>{change.old || '(empty)'}</span>
                  <span style={{ color: '#27500a', fontWeight: 500 }}>{change.new}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="sm" onClick={async () => {
                // Apply changes to client record
                const updates = {}
                Object.entries(quote.client_changes).forEach(([field, change]) => {
                  const colMap = { name: 'name', address: 'address', contact_name: 'contact_name', contact_email: 'contact_email', contact_phone: 'contact_phone', business_nature: 'notes' }
                  if (colMap[field]) updates[colMap[field]] = field === 'address' ? oneLine(change.new).trim() : change.new
                })
                await supabase.from('clients').update(updates).eq('id', quote.client_id)
                await supabase.from('quotations').update({ client_changes: null }).eq('id', quote.id)
                toast('Client details updated', 'success')
              }} style={{ background: '#eaf3de', color: '#27500a', border: '1px solid #97c459' }}>
                ✓ Accept changes
              </button>
              <button className="sm" onClick={async () => {
                await supabase.from('quotations').update({ client_changes: null }).eq('id', quote.id)
                toast('Kept the existing client details', 'info')
              }}>
                Keep existing details
              </button>
            </div>
          </div>
        )}

        {/* Full questionnaire responses - always visible */}
        <details open style={{ marginBottom: 14 }}>
          <summary style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 0', color: 'var(--text)' }}>
            Client questionnaire responses
          </summary>
          <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '12px 14px', marginTop: 8, maxHeight: 280, overflowY: 'auto', position: 'relative' }}>
            <QuestionnaireView responses={quote.form_responses} auditType={quote.audit_type} />
          </div>
          {editable && (
            <button className="sm" style={{ fontSize: 13, marginTop: 6 }}
              onClick={() => setEditResponses(true)}>
              ✎ Edit client responses
            </button>
          )}
          <AmendmentHistory auditTrail={quote.audit_trail} />
        </details>

        {priorVersions.length > 0 && (
          <div style={{ marginBottom: 14, background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
            <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8 }}>Version history ({priorVersions.length + 1})</div>
            {[quote, ...priorVersions].map((v, i) => (
              <div key={v.id} style={{ fontSize: 13, paddingBottom: 8, marginBottom: 8, borderBottom: i < priorVersions.length ? '0.5px solid var(--border)' : 'none' }}>
                <div>
                  <strong style={{ fontFamily: 'monospace' }}>{v.quote_ref}</strong>
                  {i === 0 && <span style={{ marginLeft: 6, fontSize: 13, padding: '1px 6px', borderRadius: 10, background: 'var(--brand-light)', color: 'var(--brand-dark)' }}>CURRENT</span>}
                  <span style={{ color: 'var(--text2)' }}> · {statusLabel(v)} · {mdLabel(v.mandays)} · {fmt(v.fee_ex_gst)}</span>
                </div>
                {v.revised_at && (
                  <div style={{ color: 'var(--text2)', marginTop: 2 }}>
                    Revised by {v.revised_by || '—'} on {format(parseISO(v.revised_at), 'd MMM yyyy HH:mm')}{v.revision_reason ? ` — ${v.revision_reason}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Current bizSAFE expiry — surfaced near CT17/pricing so renewal urgency is obvious. */}
        {/* (Internal audit: the certificate expiry is already in "Client questionnaire responses" above.) */}
        {other ? null : (() => {
          // Single source of truth: renewal vs new is driven by the bizSAFE expiry, not application_type.
          const exp = resolveExpiry(client, quote.form_responses)
          if (!exp) {
            return (
              <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '8px 14px', marginBottom: 14, fontSize: 13, color: 'var(--text2)' }}>
                <strong>Current bizSAFE expiry:</strong> New application — no bizSAFE expiry on file
              </div>
            )
          }
          const t = expiryTier(exp)
          const strong = t.tier === 'urgent' || t.tier === 'overdue'
          const bg = strong ? '#FDECEA' : t.tier === 'upcoming' ? '#FFF3E0' : 'var(--bg2)'
          const bd = strong ? '#E24B4A' : t.tier === 'upcoming' ? '#E6A817' : 'var(--border2)'
          const fg = strong ? '#791F1F' : t.tier === 'upcoming' ? '#854F0B' : 'var(--text2)'
          return (
            <div style={{ background: bg, border: `0.5px solid ${bd}`, borderRadius: 'var(--radius)', padding: '8px 14px', marginBottom: 14, fontSize: 13, color: fg }}>
              <strong>Current bizSAFE expiry:</strong> {format(parseISO(exp), 'd MMM yyyy')}
              {t.tier !== 'ok' && <span className={`badge ${t.badge}`} style={{ marginLeft: 8, fontSize: 13 }}>{t.label}</span>}
            </div>
          )
        })()}

        {/* Client remark / question — surfaced from the questionnaire so sales/quotation users can't miss
            it. The original also stays in the response scroll above for traceability. */}
        {(quote.form_responses?.remarks || '').trim() && (
          <div style={{ background: '#FFF8E1', border: '1px solid #E6A817', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#6b4e00' }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>📝 Client remark / question</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{quote.form_responses.remarks}</div>
          </div>
        )}

        {/* CT 17 calculation + complexity — one panel */}
        {(quote.calc_details || quote.complexity_classification) && (() => {
          const cc = (() => { try { return quote.complexity_classification ? (typeof quote.complexity_classification === 'string' ? JSON.parse(quote.complexity_classification) : quote.complexity_classification) : null } catch (e) { return null } })()
          const isPending = cc && cc.complexity === 'Pending Review'
          const tierColor = !cc ? '#0c447c'
            : cc.complexity === 'High' ? '#791F1F'
            : cc.complexity === 'Medium' ? '#854F0B'
            : cc.complexity === 'Low' ? '#27500a'
            : '#4A1D96'
          return (
            <div style={{ background: '#e6f1fb', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#0c447c' }}>
              {quote.calc_details && (
                <div>
                  <strong>CT 17 calculation:</strong> {quote.audit_type} · {quote.calc_details.totalMandays || quote.calc_details.mandays} man-day(s)
                  {quote.calc_details.requiresLeadAuditor && ' · Lead Auditor required'}
                  {quote.calc_details.numSitesToVisit > 1 && ` · ${quote.calc_details.numSitesToVisit} sites to visit`}
                </div>
              )}
              {cc && (
                <div style={{ marginTop: quote.calc_details ? 6 : 0, paddingTop: quote.calc_details ? 6 : 0, borderTop: quote.calc_details ? '0.5px solid rgba(12,68,124,0.15)' : 'none' }}>
                  <strong>Complexity:</strong> <span style={{ fontWeight: 600, color: tierColor }}>{cc.complexity}</span>
                  <span style={{ opacity: 0.85 }}> — {complexityBasisText(cc)}</span>
                  {isPending && <div style={{ fontWeight: 600, color: '#4A1D96', marginTop: 4 }}>⚠ Complexity needs to be confirmed before issuing this quotation (use “Override classification” below).</div>}
                </div>
              )}
            </div>
          )
        })()}

        {/* Complexity provenance + override (traceability) */}
        {quote.complexity_classification && (() => {
          const cc = (() => { try { return typeof quote.complexity_classification === 'string' ? JSON.parse(quote.complexity_classification) : quote.complexity_classification } catch (e) { return null } })()
          if (!cc) return null
          const isPending = cc.complexity === 'Pending Review'
          // When complexity is unconfirmed, the override is REQUIRED — show it expanded in a red box
          // immediately (same pattern as the man-day justification), not hidden behind a small button.
          const showBox = editable && (isPending || showComplexityOverride)
          return (
            <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text2)' }}>
              {!editable ? null : !showBox ? (
                <button className="sm" style={{ marginTop: 6 }} onClick={() => { setShowComplexityOverride(true); setOverrideVal(cc.complexity || '') }}>Override classification</button>
              ) : (
                <div style={{ marginTop: 8, padding: 12, borderRadius: 8, background: isPending ? '#FDECEA' : 'var(--bg2)', border: isPending ? '0.5px solid #E24B4A' : 'none' }}>
                  {isPending && <div style={{ fontWeight: 600, color: '#791F1F', marginBottom: 8 }}>⚠ Complexity must be confirmed before this quotation can be issued — select the classification below.</div>}
                  <div className="field"><label>Complexity classification</label>
                    <select value={overrideVal} onChange={e => setOverrideVal(e.target.value)}>
                      {['', 'High', 'Medium', 'Low'].map(o => <option key={o} value={o}>{o || '— select —'}</option>)}
                    </select>
                  </div>
                  <div className="field"><label style={{ color: 'var(--danger)' }}>Reason {isPending ? 'for confirming the classification' : 'for override'} (required) *</label>
                    <textarea rows={2} value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Why is this the correct complexity?" />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="primary sm" disabled={savingOverride || !overrideVal || !overrideReason.trim()} onClick={saveComplexityOverride}>Save classification</button>
                    {!isPending && <button className="sm" onClick={() => { setShowComplexityOverride(false); setOverrideReason('') }}>Cancel</button>}
                  </div>
                </div>
              )}
            </div>
          )
        })()}

        {!other && <div style={{ marginBottom: 12 }}><CT17Reference /></div>}

        {ia && (
          <div className="field" style={{ maxWidth: 260 }}>
            <label>Contract</label>
            <select value={contractYears} onChange={e => setContractYears(parseInt(e.target.value, 10))} disabled={!editable}>
              {IA_CONTRACT_OPTIONS.map(y => <option key={y} value={y}>{y === 1 ? '1 year (single audit)' : `${y} years (1 audit a year)`}</option>)}
            </select>
          </div>
        )}

        {pickLevel && (
          <div className="field" style={{ maxWidth: 260 }}>
            <label>Applying for</label>
            <select value={level} onChange={e => setLevel(e.target.value)} disabled={!editable}>
              {APPLYING_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}

        {/* Man-days with justification */}
        <div className="grid-2">
          {/* OTE: fixed fee — the man-days (0.5) are set by the questionnaire and not shown here. */}
          {!ote && <div className="field">
            <label>{ia ? (contractYears > 1 ? 'Man-days per year *' : 'Man-days *') : `Man-days (CT 17 calculated: ${ctMandays || '—'})`}</label>
            <input type="number" step="0.5" value={mandays} onChange={e => setMandays(e.target.value)} onWheel={e => e.currentTarget.blur()} />
          </div>}
          <div className="field">
            <label>{ia && contractYears > 1 ? 'Fees ex-GST (SGD) per year *' : 'Fee ex-GST (SGD) *'}</label>
            <input type="number" step="0.01" value={fee} onChange={e => setFee(e.target.value)} onWheel={e => e.currentTarget.blur()} placeholder="940.00" />
            {feeSuggestion && !other && (
              <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>
                Suggested: <b>${feeSuggestion.amount.toLocaleString()}</b> — {feeSuggestion.source}
                {' '}(${feeSuggestion.ratePerHalf.toLocaleString()}/half man-day × {mandays} man-days)
                {String(fee) !== String(feeSuggestion.amount) && (
                  <button className="sm" style={{ marginLeft: 8 }} onClick={() => setFee(String(feeSuggestion.amount))}>Use</button>
                )}
              </div>
            )}
          </div>
        </div>

        {belowBaseline && (
          <div className="field">
            <label style={{ color: 'var(--danger)' }}>Justification for reducing man-days below the CT 17 baseline ({ctMandays}) — required *</label>
            <textarea value={mandayJustification} onChange={e => setMandayJustification(e.target.value)}
              placeholder="e.g. Reduced from 1.0 to 0.5 man-day as client has simple operations with minimal hazards…" rows={2} />
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>This deviation is recorded to the audit trail when you send the quotation.</div>
          </div>
        )}

        {fee > 0 && (
          <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, marginBottom: 14, display: 'flex', gap: 24 }}>
            <span>Ex-GST: <strong>{fmt(feeNum)}</strong></span>
            <span>Inc 9% GST: <strong>{fmt(feeInc)}</strong></span>
          </div>
        )}

        <div className="grid-2">
          <div className="field"><label>Quote validity (days)</label>
            <input type="number" value={validity} onChange={e => setValidity(e.target.value)} onWheel={e => e.currentTarget.blur()} /></div>
          {!other && <div className="field"><label>Estimated timeline</label>
            <input value={timeline} onChange={e => setTimeline(e.target.value)} /></div>}
        </div>
        <div className="field"><label>Remarks / special notes</label>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2} /></div>

        <div className="modal-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          {complexityPending && (
            <div style={{ flexBasis: '100%', fontSize: 13, color: '#4A1D96', padding: '8px 10px', background: '#F3F0FF', borderRadius: 6 }}>
              ⚠ Complexity is Pending Review — confirm or override the complexity above before this quotation can be sent.
            </div>
          )}
          {justificationMissing && !complexityPending && (
            <div style={{ flexBasis: '100%', fontSize: 13, color: '#854F0B', padding: '8px 10px', background: '#FFF3E0', borderRadius: 6 }}>
              ⚠ Man-days are below the CT 17 baseline ({ctMandays}) — enter a justification above before sending.
            </div>
          )}
          {!editable && (
            <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>
              {statusLabel(quote)} — read-only. Use “Revise” to issue a new version.
            </span>
          )}
          {editable && canSend && !other && (
            <div style={{ flexBasis: '100%', fontSize: 13, color: 'var(--text3)', lineHeight: 1.5 }}>
              By sending this quotation, the AO confirms the application has been reviewed, the audit scope is understood,
              and the audit duration / man-days have been determined in accordance with CT17. Acceptance (by &amp; date) is recorded automatically.
            </div>
          )}
          {quote.application_accepted_at && (
            <div style={{ flexBasis: '100%', fontSize: 13, color: 'var(--text3)' }}>
              Application accepted via quotation — by {quote.application_accepted_by || '—'} · {new Date(quote.application_accepted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
            </div>
          )}
          <button onClick={requestClose}>Close</button>
          {effectiveStatus(quote) === 'Draft' && (
            <button onClick={() => save('draft')} disabled={saving}>Save draft</button>
          )}
          {editable && (
            <>
              <button onClick={() => save('send-outlook')}
                disabled={saving || !fee || !canSend}
                title={!canSend ? (complexityPending ? 'Confirm complexity first' : 'Justification required') : 'Marks as Sent and copies the sign link — compose the email yourself in Outlook (use for emails with attachments, e.g. a consultant’s quote)'}>
                Send via Outlook
              </button>
              <button className="primary" onClick={() => save('send')}
                disabled={saving || !fee || !canSend}
                title={!canSend ? (complexityPending ? 'Confirm complexity first' : 'Justification required') : 'System sends the quotation email — tracked, replies land in Zoho Desk'}>
                Send via system
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    {editResponses && (
      <EditResponsesModal
        quote={quote}
        onClose={() => setEditResponses(false)}
        onSaved={saved => {
          // Sync the editable man-days field to the freshly recalculated CT17 value,
          // unless the preparer manually changed it away from the previous baseline.
          const untouched = mandays === '' || parseFloat(mandays) === parseFloat(ctMandays)
          if (untouched && saved.mandays != null) setMandays(saved.mandays)
          if (onUpdate) onUpdate(saved)
          setEditResponses(false)
        }}
      />
    )}
    </>
  )
}

// Quotation preview (what client sees)
function QuotePreview({ quote, client, onClose }) {
  const r = quote.form_responses || {}
  const contactName = r.pic_name || client?.contact_name || ''
  const contactEmail = r.pic_email || client?.contact_email || r.email || ''
  const contactPhone = r.pic_phone || client?.contact_phone || r.phone || ''
  const businessNature = r.business_nature || client?.notes || client?.industry || ''
  const companyName = quoteCompanyName(quote, client)
  const companyAddress = oneLine(r.company_address || client?.address || '')   // one line (older ones had breaks)
  const numEmp = r.num_employees || '—'
  const md = mdLabel(quote.mandays)

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 660 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Quotation preview — {quote.quote_ref}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: 500 }}>{companyName}</div>
              <div style={{ color: 'var(--text2)' }}>{companyAddress}</div>
              {contactName && <div style={{ color: 'var(--text2)' }}>{contactName}</div>}
              {(contactPhone || contactEmail) && <div style={{ color: 'var(--text2)' }}>{contactPhone && `T: ${contactPhone}`}{contactPhone && contactEmail && ' · '}{contactEmail && `E: ${contactEmail}`}</div>}
            </div>
            <div style={{ textAlign: 'right', color: 'var(--text2)', fontSize: 13 }}>
              <div>Ref: {quote.quote_ref}</div>
              {quote.sent_date && <div>{format(parseISO(quote.sent_date), 'd MMM yyyy')}</div>}
            </div>
          </div>

          <div style={{ fontWeight: 500, marginBottom: 14 }}>RE: {quoteSchemeTitle(quote)}</div>

          {(isIAQuote(quote)
            ? [['FEES', iaFeeText(quote, fmt)], ...iaQuoteRows(quote, client).map(([l, v]) => [l.toUpperCase(), v]), ['QUOTE VALIDITY', `Good for ${quote.validity_days || 30} days only`]]
            : isOTEQuote(quote) ? oteQuotePreviewRows(quote, client, fmt)
            : [
            ['AA. FEES', `${fmt(quote.fee_ex_gst)} w/o GST\n(${fmt(quote.fee_inc_gst)} w/ 9% GST) per audit`],
            ['BB. SCOPE', `WSH Risk Management Audit (${md})\n• Arrange WSH RM Audit by SAC Accredited Auditing Organisation (SAC-AO)\n• Issuance of WSH RM Audit report${quoteShowsWshcSubmission(quote) ? '\n• Report submission to WSHC' : ''}`],
            ['CC. BUSINESS NATURE', businessNature || '—'],
            ['DD. NO. OF EMPLOYEES', numEmp],
            ['EE. bizSAFE EXPIRY', (r.expiry_date || r.expiry_date_progression) ? format(parseISO(r.expiry_date || r.expiry_date_progression), 'd MMM yyyy') : 'NA'],
            ['FF. PAYMENT TERMS', '100% Upfront Payment\nGIRO / PayNow Preferred – UEN 199205611D'],
            ['GG. TIMELINE', quote.timeline || '~2 months'],
            ['HH. QUOTE VALIDITY', `Good for ${quote.validity_days || 30} days only`],
            ['II. REMARKS', `• Ensure your company is NOT SgMA certified by SCAL\n• Please confirm preferred audit date at least 1 month in advance\n• Only for Level 3 or 4 — ensure Level 1 and 2 certificate holders are present${quote.remarks ? '\n• ' + quote.remarks : ''}`],
          ]).map(([s, v]) => (
            <div key={s} style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 500, fontSize: 13, background: 'var(--bg2)', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginBottom: 2 }}>{s}</div>
              <div style={{ whiteSpace: 'pre-line', paddingLeft: 8, color: 'var(--text)' }}>{v}</div>
            </div>
          ))}

          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--text2)' }}>
            Full terms & conditions: {isIAQuote(quote)
              ? <a href="/terms/internal-audit" target="_blank" rel="noreferrer">Internal Audit terms &amp; conditions</a>
              : isOTEQuote(quote) ? <a href="/terms/ote" target="_blank" rel="noreferrer">OTE terms &amp; conditions</a>
              : <a href="/terms" target="_blank">ccissg.netlify.app/terms</a>}
          </div>
          {quote.audit_trail?.signatory_name && quote.audit_trail?.signed_at && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#eaf3de', borderRadius: 'var(--radius)', fontSize: 13 }}>
              <div style={{ fontWeight: 500, color: '#27500a', marginBottom: 8 }}>✓ Signed — Acceptance audit trail</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '4px 16px', color: '#3b6d11' }}>
                <div>Signatory: <strong>{quote.audit_trail.signatory_name}</strong></div>
                <div>Designation: <strong>{quote.audit_trail.signatory_designation}</strong></div>
                <div>Date & time: <strong>{new Date(quote.audit_trail.signed_at).toLocaleString('en-SG')}</strong></div>
                <div>IP address: <strong>{quote.audit_trail.ip_address}</strong></div>
              </div>
            </div>
          )}
          {/* SAC traceability (office-only): how the CT 17 complexity was determined — including any manual
              override's who/when/reason — plus the man-day justification, permanently visible from Preview
              at every quote status. The client-facing quotation (email/signing page) never includes this. */}
          {(() => {
            const cc = (() => { try { return quote.complexity_classification ? (typeof quote.complexity_classification === 'string' ? JSON.parse(quote.complexity_classification) : quote.complexity_classification) : null } catch (e) { return null } })()
            if (!cc && !(quote.manday_justification || '').trim()) return null
            return (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg2)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--text2)' }}>
                <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>Internal — SAC traceability (not shown to client)</div>
                {cc && <div><strong>Complexity:</strong> {cc.complexity} — {complexityBasisText(cc)}</div>}
                {(quote.manday_justification || '').trim() && <div style={{ marginTop: 4 }}><strong>Man-day justification:</strong> {quote.manday_justification}</div>}
              </div>
            )
          })()}
        </div>

        {quote.signature_token && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--accent-bg)', borderRadius: 'var(--radius)', fontSize: 13 }}>
            <div style={{ color: 'var(--accent-text)', marginBottom: 4 }}>Client signing link:</div>
            <div style={{ wordBreak: 'break-all', color: 'var(--accent)' }}>{window.location.origin}/sign/{quote.signature_token}</div>
            <button className="sm" style={{ marginTop: 6 }} onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sign/${quote.signature_token}`)}>Copy link</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// Read-only version history — viewable for ANY status (a revised Sent quote has no Prepare/Edit button).
// Shows each version's ref/status/man-days/fee plus who revised, when, and why.
function VersionHistoryModal({ quote, versions, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 14 }}>Version history — {quote.quote_ref}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: '4px 0 12px' }}>Newest first. Each revision supersedes the one before it; the top one is the active version.</p>
        {[quote, ...versions].map((v, i) => (
          <div key={v.id} style={{ fontSize: 13, paddingBottom: 10, marginBottom: 10, borderBottom: i < versions.length ? '0.5px solid var(--border)' : 'none' }}>
            <div>
              <strong style={{ fontFamily: 'monospace' }}>{v.quote_ref}</strong>
              {i === 0 && <span style={{ marginLeft: 6, fontSize: 13, padding: '1px 6px', borderRadius: 10, background: 'var(--brand-light)', color: 'var(--brand-dark)' }}>CURRENT</span>}
              <span style={{ color: 'var(--text2)' }}> · {statusLabel(v)} · {mdLabel(v.mandays)} · {fmt(v.fee_ex_gst)}</span>
            </div>
            {v.revised_at ? (
              <div style={{ color: 'var(--text2)', marginTop: 3 }}>
                Revised by {v.revised_by || '—'} on {format(parseISO(v.revised_at), 'd MMM yyyy HH:mm')}{v.revision_reason ? ` — ${v.revision_reason}` : ''}
              </div>
            ) : <div style={{ color: 'var(--text3)', marginTop: 3 }}>Original version</div>}
          </div>
        ))}
        <div className="modal-footer"><button className="primary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  )
}

export default function Quotations() {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState([])
  const [clients, setClients] = useState({})
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const [filter, setFilter] = useState(['to_prepare', 'awaiting', 'working', 'history', 'all'].includes(searchParams.get('filter')) ? searchParams.get('filter') : 'to_prepare')
  const [search, setSearch] = useState('')
  // 'biz' (default, always first) | 'ia' | 'ote' — ?service=ote opens a tab directly (the OTE questionnaire email links here)
  const [service, setService] = useState(['ia', 'ote'].includes(searchParams.get('service')) ? searchParams.get('service') : 'biz')
  const [oteLinks, setOteLinks] = useState(null)   // OTE questionnaires sent, not answered yet
  const [sendOte, setSendOte] = useState(false)
  const [editModal, setEditModal] = useState(null)
  const [editResponsesModal, setEditResponsesModal] = useState(null)
  const [previewModal, setPreviewModal] = useState(null)
  const [historyModal, setHistoryModal] = useState(null)
  const [questionnaireModal, setQuestionnaireModal] = useState(null)   // read-only client responses, any status
  const [outcomeMenu, setOutcomeMenu] = useState(null)                 // which Sent quote's Outcome menu is open
  const [outcomePos, setOutcomePos] = useState(null)                   // viewport coords so the menu floats free of the scroll area (never clipped)
  const { profile, role, logAction } = useAuth()
  const currentUser = profile?.name || localStorage.getItem('user_name') || 'Staff'
  const currentRole = role || 'Staff'

  useEffect(() => { load(true) }, [])
  // OTE tab: questionnaires sent but not answered yet (so the office sees who to chase).
  useEffect(() => {
    if (service !== 'ote' || sendOte) return
    supabase.from('ote_form_links').select('token, sent_to, created_at, client_id, clients(name)').is('submitted_at', null)
      .order('created_at', { ascending: false }).limit(50).then(({ data }) => setOteLinks(data || []))
  }, [service, sendOte])

  // Refresh when the tab regains focus — so a quote the client just signed (on the public link) drops out
  // of the working list without a manual refresh. Once per return (browsers fire two events), in the background.
  useEffect(() => onTabReturn(() => load()), [])

  // First visit: a LIGHT list (only what the table shows) appears first, while the full details load alongside and
  // then replace it (measured: ~0.3 s vs ~0.55 s). Until the full details are in, the row buttons are disabled
  // (q._lite), so every popup / action always gets the complete quotation — exactly as before.
  async function load(firstVisit = false) {
    let fullDone = false
    if (firstVisit) {
      supabase.from('quotations')
        .select('id, created_at, client_id, prospect_name, prospect_uen, audit_type, quote_ref, status, fee_ex_gst, mandays, validity_days, sent_date, signed_date, job_id, prepared_by, revision_of, clients(id, name)')
        .order('created_at', { ascending: false })
        .then(({ data }) => {
          if (fullDone || !data) return
          setQuotes(data.map(q => ({ ...q, _lite: true })))
          setLoading(false)
        }, () => {})
    }
    const { data } = await supabase.from('quotations')
      .select('id, created_at, client_id, prospect_name, prospect_uen, audit_type, quote_ref, status, fee, fee_ex_gst, fee_inc_gst, mandays, calc_details, complexity_classification, client_changes, form_responses, business_nature, num_employees, num_locations, application_type, bizsafe_level, audit_scope, validity_days, timeline, remarks, sent_date, signed_date, signatory_name, signatory_designation, signature_token, job_id, prepared_by, manday_justification, audit_trail, preferred_dates, revision_of, revised_by, revised_at, revision_reason, clients(id, name, contact_name, contact_email, contact_phone, address, industry, notes, bizsafe_expiry_date)')
      .order('created_at', { ascending: false })
    fullDone = true
    // For quotations without a client (prospects), use form_responses data
    const qs = data || []
    setQuotes(qs)
    const cl = {}
    qs.forEach(q => { if (q.clients) cl[q.client_id] = q.clients })
    setClients(cl)
    setLoading(false)
  }

  // Update the row AND the currently-open modal in place, without closing it.
  function handleUpdate(updated) {
    setQuotes(qs => qs.map(q => q.id === updated.id ? { ...q, ...updated } : q))
    setEditModal(em => (em && em.id === updated.id) ? { ...em, ...updated } : em)
  }

  async function handleSaved(updated) {
    setQuotes(qs => qs.map(q => q.id === updated.id ? { ...q, ...updated } : q))
    setEditModal(null)
    // If sent: deliver per the chosen mode (Send via system / Send via Outlook).
    if (updated.status === 'Sent' && updated.signature_token) {
      const c = clients[updated.client_id]
      const link = `${window.location.origin}/sign/${updated.signature_token}`
      const ia = isIAQuote(updated)
      const ote = isOTEQuote(updated)
      // Client internal audit and OTE quotes have their own approved wording; bizSAFE uses the Settings template as before.
      const tmpl = ia
        ? iaQuoteEmail({ company: c?.name || updated.form_responses?.company_name || updated.prospect_name || '',
            contact: updated.form_responses?.pic_name || c?.contact_name || '', quoteRef: updated.quote_ref,
            link, validityDays: updated.validity_days || 30 })
        : ote
        // OTE: the questionnaire's contact person comes first (the OTE PIC is kept on the job, never the client record).
        ? oteQuoteEmail({ company: c?.name || updated.form_responses?.company_name || updated.prospect_name || '',
            contact: updated.form_responses?.pic_name || c?.contact_name || '', quoteRef: updated.quote_ref,
            link, validityDays: updated.validity_days || OTE_DEFAULT_VALIDITY })
        : applyTemplate(getTemplate('quotation'), {
        CLIENT_NAME: c?.contact_name || updated.form_responses?.pic_name || c?.name || '',
        COMPANY_NAME: quoteCompanyName(updated, c),   // the template's subject uses it — was never filled in
        QUOTE_REF: updated.quote_ref,
        AUDIT_TYPE: updated.audit_type || '',
        SIGN_LINK: link,
        VALIDITY_DAYS: String(updated.validity_days || 30),
        FIRM_NAME: FIRM_NAME(),
      })
      try { navigator.clipboard.writeText(link) } catch (e) {}   // link always on the clipboard as backup
      // IA and OTE: the person on that service's form comes first (never the client's main bizSAFE contact by default).
      const to = (ote || ia) ? (updated.form_responses?.pic_email || c?.contact_email || '') : (c?.contact_email || updated.form_responses?.pic_email || '')
      // Same CCIS email design as every other AuditPM email; the sign link (own line in the template) is a button.
      const htmlBody = textEmailHtml({ title: `Quotation ${updated.quote_ref}`, text: tmpl.body, buttonLabel: 'View & accept quotation →' })
      // Two send modes (chosen via the modal buttons):
      //  'send'         -> system sends via send-client-email (tracked; replies thread into Zoho Desk)
      //  'send-outlook' -> nothing is emailed; sign link is on the clipboard for a hand-written
      //                    Outlook email (used when attachments are needed, e.g. a consultant's quote)
      if (updated._sendMode === 'send-outlook') {
        toast('Sign link copied', 'success', { detail: 'Paste it into your Outlook email. The quote is now marked Sent.' })
        return
      }
      let systemSent = false
      if (to) {
        try {
          // Internal audit: replies go to the client owner (resolved by the server); a first-time prospect with no
          // client record yet → replies go to the staff member sending it. Never bizSAFE@ / Zoho Desk.
          let iaReply = {}
          if (ia) {
            if (updated.client_id) iaReply = { reply_to_owner: true }
            else { const { data: me } = await supabase.auth.getUser(); iaReply = { reply_to: me?.user?.email || undefined } }
          }
          const { data: res, error: fnErr } = await supabase.functions.invoke('send-client-email', {
            body: { to, subject: tmpl.subject, html: htmlBody, category: ia ? 'ia_quote' : ote ? 'ote_quote' : 'quote',
                    client_id: updated.client_id || null, quote_ref: updated.quote_ref, ...iaReply } })
          if (!fnErr && res && res.ok) systemSent = true
        } catch (e) { /* handled below */ }
      }
      if (systemSent) {
        toast(`Quotation emailed to ${to}`, 'success')
      } else {
        toast("The quotation wasn't emailed", 'error', { detail: 'The sign link is copied — send it from Outlook' })
      }
    }
  }

  async function reviseQuote(q) {
    const reason = window.prompt(`Revise ${q.quote_ref} — reason for revision (required):`)
    if (reason === null) return
    if (!reason.trim()) { toast('Enter a revision reason', 'warning'); return }
    // Work out the new revision ref (pm/1501-26/WT → pm/1501r1-26/WT → r2 …)
    let baseRef = q.quote_ref || ''
    let newRef = ''
    const revMatch = baseRef.match(/^([a-z]+\/[0-9]+)r([0-9]+)(-[0-9]+\/.+)$/)
    const origMatch = baseRef.match(/^([a-z]+\/[0-9]+)(-[0-9]+\/.+)$/)
    if (revMatch) newRef = `${revMatch[1]}r${parseInt(revMatch[2]) + 1}${revMatch[3]}`
    else if (origMatch) newRef = `${origMatch[1]}r1${origMatch[2]}`
    else newRef = baseRef + 'r1'
    // Create the new active version (Draft), linked to the version it supersedes
    const { data: newQuote, error: insErr } = await supabase.from('quotations').insert({
      client_id: q.client_id, prospect_name: q.prospect_name, prospect_uen: q.prospect_uen,
      audit_type: q.audit_type, quote_ref: newRef, status: 'Draft',
      form_responses: q.form_responses, mandays: q.mandays, calc_details: q.calc_details,
      complexity_classification: q.complexity_classification,
      business_nature: q.business_nature, num_employees: q.num_employees, num_locations: q.num_locations,
      application_type: q.application_type, bizsafe_level: q.bizsafe_level, audit_scope: q.audit_scope,
      fee: q.fee, fee_ex_gst: q.fee_ex_gst, fee_inc_gst: q.fee_inc_gst,
      validity_days: q.validity_days, timeline: q.timeline, remarks: q.remarks,
      ...(q.ia_contract_years ? { ia_contract_years: q.ia_contract_years } : {}),
      prepared_by: currentUser, prepared_by_role: currentRole, manday_justification: q.manday_justification,
      revision_of: q.id, revised_by: currentUser, revised_at: new Date().toISOString(), revision_reason: reason.trim(),
    }).select('id').single()
    if (insErr) { failed("Couldn't create the revision", insErr.message); return }
    // If the previous version was still active (Draft/Sent), supersede it so its sign link dies.
    // Inactive heads (Declined / Withdrawn / Signed) keep their real outcome status in history.
    let superseded = false
    if (['Draft', 'Sent'].includes(q.status)) {
      const { error: supErr } = await supabase.from('quotations').update({ status: 'Superseded' }).eq('id', q.id)
      if (supErr) failed('Revision created, but the old one is still active', supErr.message, 'Send a screenshot to the admin')
      else superseded = true
    }
    if (logAction) await logAction('Quotation revised', 'quotation', newQuote.id, newRef, { supersedes: q.quote_ref, reason: reason.trim(), by: currentUser })
    await load()
    toast(`Revision ${newRef} created`, 'success', { detail: superseded ? 'The old version can’t be signed any more' : '' })
  }

  async function setQuoteStatus(q, status, label) {
    if (!window.confirm(`Mark ${q.quote_ref} as ${label}? It will move out of the working register.`)) return
    const { error } = await supabase.from('quotations').update({ status }).eq('id', q.id)
    if (error) { failed("Couldn't change the quote status", error.message); return }
    if (logAction) await logAction(`Quotation ${label.toLowerCase()}`, 'quotation', q.id, q.quote_ref, { by: currentUser })
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status } : x))
  }

  // Archive a submitted enquiry we've chosen not to quote (competitor, no response to a request for more info).
  // Nothing is deleted — the record and its questionnaire are preserved; the row simply moves to
  // History / archive. Restore brings it back to "To prepare" if the client ever re-engages.
  async function archiveQuote(q) {
    if (!window.confirm(`Archive ${q.quote_ref}? It moves to History / archive (not deleted) — the questionnaire is kept, and you can restore it later.`)) return
    const { error } = await supabase.from('quotations').update({ status: 'Archived' }).eq('id', q.id)
    if (error) { failed("Couldn't archive", error.message); return }
    if (logAction) await logAction('Quotation archived', 'quotation', q.id, q.quote_ref, { by: currentUser })
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status: 'Archived' } : x))
  }

  async function restoreQuote(q) {
    if (!window.confirm(`Restore ${q.quote_ref} to "To prepare"?`)) return
    const { error } = await supabase.from('quotations').update({ status: 'Draft' }).eq('id', q.id)
    if (error) { failed("Couldn't restore", error.message); return }
    if (logAction) await logAction('Quotation restored from archive', 'quotation', q.id, q.quote_ref, { by: currentUser })
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status: 'Draft' } : x))
  }

  async function deleteQuote(q) {
    if (!window.confirm(`Delete quotation ${q.quote_ref}? This cannot be undone.`)) return
    const { error } = await supabase.from('quotations').delete().eq('id', q.id)
    if (error) { failed("Couldn't delete", error.message); return }
    setQuotes(qs => qs.filter(x => x.id !== q.id))
  }

  async function markSigned(q) {
    if (effectiveStatus(q) !== 'Sent') {
      toast('Only the current version can be marked signed', 'warning')
      return
    }
    const today = new Date().toISOString().split('T')[0]
    // Client internal audit, first-time prospect (no client record yet): find the client by UEN or create it —
    // exactly what online signing does — so the job (and its internal audit record) belongs to a real client.
    if ((isIAQuote(q) || isOTEQuote(q)) && !q.client_id) {
      const r = q.form_responses || {}
      // UEN compared as stored on the Clients page: upper case, no spaces (clientMatch.normUen).
      const uen = String(r.uen || q.prospect_uen || '').replace(/\s+/g, '').toUpperCase()
      let clientId = null
      if (uen) {
        const { data: found } = await supabase.from('clients').select('id').ilike('uen', uen).limit(1)
        clientId = found?.[0]?.id || null
      }
      if (!clientId) {
        const { data: created, error: cErr } = await supabase.from('clients').insert({
          name: r.company_name || q.prospect_name || 'New client', uen: uen || null,
          contact_name: r.pic_name || null, contact_email: r.pic_email || null, contact_phone: r.pic_phone || null,
          address: r.company_address || null, notes: r.business_nature || null,
          // Internal Audit: the form's contact is also the client's IA contact (IA_CONTACT.sql).
          ...(isIAQuote(q) ? { ia_contact_name: r.pic_name || null, ia_contact_email: r.pic_email || null, ia_contact_phone: r.pic_phone || null } : {}),
        }).select('id').single()
        if (cErr) { failed("Couldn't create the client", cErr.message); return }
        clientId = created.id
      }
      const { error: linkErr } = await supabase.from('quotations').update({ client_id: clientId }).eq('id', q.id)
      if (linkErr) { failed("Couldn't link the client", linkErr.message); return }
      q = { ...q, client_id: clientId }
    }
    const { error: upErr } = await supabase.from('quotations').update({ status: 'Signed', signed_date: today }).eq('id', q.id)
    if (upErr) { failed("Couldn't mark it signed", upErr.message); return }
    // Reuse an existing job for this quote_ref if present (avoid duplicate job)
    let jobId = q.job_id || null
    if (!jobId && q.quote_ref) {
      const { data: existing } = await supabase.from('jobs').select('id').eq('quote_ref', q.quote_ref).maybeSingle()
      if (existing) jobId = existing.id
    }
    if (!jobId) {
      const { data: job, error: jobErr } = await supabase.from('jobs').insert({
        client_id: q.client_id,
        audit_type: q.audit_type,
        scope: q.audit_scope || '',
        mandays: q.mandays,
        status: 'Confirmed',
        quote_ref: q.quote_ref,
        quote_amount: q.fee_ex_gst,
        quote_sent_date: q.sent_date,
        signed_date: today,
        signature_token: q.signature_token,   // so the job can always link back to the signed agreement
        form_data: q.form_responses,
        business_nature: q.business_nature || q.form_responses?.business_nature,
        audit_site_address: '',
        complexity_classification: q.complexity_classification || null,
        manday_justification: q.manday_justification || null,
        notes: `Auto-created from quotation ${q.quote_ref}. Signed on ${today}.`,
      }).select('id').single()
      if (jobErr) { failed("Couldn't create the job", jobErr.message); return }
      jobId = job?.id
    }
    if (jobId) await supabase.from('quotations').update({ job_id: jobId }).eq('id', q.id)
    setQuotes(qs => qs.map(x => x.id === q.id ? { ...x, status: 'Signed', signed_date: today, job_id: jobId, client_id: q.client_id } : x))
    // Best-effort internal notification to the quotation owner (+ accounts). NEVER blocks the office flow.
    try {
      const company = quoteCompanyName(q, clients[q.client_id]) || '—'
      const initials = (q.quote_ref || '').split('/')[2] || null
      await supabase.functions.invoke('send-internal-email', {
        body: {
          initials,
          to: 'query@ccis.com.sg',
          cc: ['accounts@ccis.com.sg'],
          subject: `Quotation Signed – ${q.quote_ref} – ${company}`,
          html: emailHtml({
            title: 'Quotation signed',
            greeting: 'Dear {{staff_name}},',
            lines: ['The following quotation has been signed by the client.', 'Accounts: please arrange invoicing and update the CRM records accordingly.'],
            details: [['Quotation reference', q.quote_ref], ['Company', company], ['Audit type', q.audit_type || '—'],
              ['Signed date', new Date(today).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })]],
            button: { label: 'Open job →', url: `${window.location.origin}${jobId ? `/jobs/${jobId}` : '/jobs'}` },
          }),
        },
      })
    } catch (e) { console.error('mark-signed notification failed (non-blocking):', e) }
    if (jobId && window.confirm('Job created! Go to job now?')) navigate(`/jobs/${jobId}`)
  }

  function statusBadge(q) {
    const cls = { Draft: 'badge-gray', Sent: 'badge-warn', Signed: 'badge-ok', Superseded: 'badge-gray', Expired: 'badge-danger', Declined: 'badge-gray', Withdrawn: 'badge-gray', Archived: 'badge-gray', Cancelled: 'badge-danger' }
    const s = effectiveStatus(q)
    return <span className={`badge ${cls[s] || 'badge-gray'}`}>{statusLabel(q)}</span>
  }

  // Group versions into families by reference (works for linked revisions AND legacy ones
  // where revision_of was never populated). Only the latest version of each family is a
  // "head" shown in the register; older versions live in the head's Version history.
  const families = {}
  quotes.forEach(q => { const k = familyKey(q); (families[k] = families[k] || []).push(q) })
  const byRevDesc = (a, b) => (revNum(b) - revNum(a)) || (new Date(b.created_at) - new Date(a.created_at))
  const headIds = new Set()
  Object.values(families).forEach(members => { const head = members.slice().sort(byRevDesc)[0]; if (head) headIds.add(head.id) })
  // bizSAFE / Internal Audit switch: filters the already-loaded list (no extra loading).
  const heads = quotes.filter(q => headIds.has(q.id) && (service === 'ia' ? isIAQuote(q) : service === 'ote' ? isOTEQuote(q) : !isIAQuote(q) && !isOTEQuote(q)))
  const chainFor = (q) => q ? (families[familyKey(q)] || []).filter(v => v.id !== q.id).sort(byRevDesc) : []

  const counts = {
    working: heads.filter(isActiveQuote).length,
    to_prepare: heads.filter(q => effectiveStatus(q) === 'Draft').length,
    awaiting: heads.filter(q => effectiveStatus(q) === 'Sent').length,
    history: heads.filter(q => !isActiveQuote(q)).length,
    all: heads.length,
  }

  const filtered = heads.filter(q => {
    const c = clients[q.client_id] || q.clients   // q.clients: the light list (first visit), until the full one is in
    // Search the SAME name that's displayed (quoteCompanyName), so prospect/expired quotes with no linked
    // client record are findable by their company name — plus the quote ref and prospect UEN.
    const term = search.trim().toLowerCase()
    const matchSearch = !term
      || quoteCompanyName(q, c).toLowerCase().includes(term)
      || (q.quote_ref || '').toLowerCase().includes(term)
      || (q.prospect_uen || '').toLowerCase().includes(term)
    if (!matchSearch) return false
    if (filter === 'to_prepare') return effectiveStatus(q) === 'Draft'   // submitted enquiries awaiting a quote
    if (filter === 'awaiting') return effectiveStatus(q) === 'Sent'       // sent, awaiting client signature
    if (filter === 'working') return isActiveQuote(q)
    if (filter === 'history') return !isActiveQuote(q)
    return true // all
  })

  // History / archive: order by most-recently-signed first (falls back to created_at for archived
  // rows that were never signed — Declined / Withdrawn / Expired). Other tabs keep created_at order.
  const signedSortKey = q => q.signed_date || q.created_at || ''
  const rows = filter === 'history'
    ? [...filtered].sort((a, b) =>
        (new Date(signedSortKey(b)) - new Date(signedSortKey(a))) ||
        (new Date(b.created_at) - new Date(a.created_at)))
    : filtered

  return (
    <div>
      {editModal && <QuoteModal quote={editModal} client={clients[editModal.client_id]} onClose={() => setEditModal(null)} onSaved={handleSaved} onUpdate={handleUpdate} priorVersions={chainFor(editModal)} />}
      {editResponsesModal && <EditResponsesModal quote={editResponsesModal} onClose={() => setEditResponsesModal(null)} onSaved={saved => { setQuotes(qs => qs.map(q => q.id === saved.id ? { ...q, ...saved } : q)); setEditResponsesModal(null) }} />}
      {previewModal && <QuotePreview quote={previewModal} client={clients[previewModal.client_id]} onClose={() => setPreviewModal(null)} />}
      {questionnaireModal && (
        <div className="modal-overlay" onClick={() => setQuestionnaireModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: 14 }}>Client questionnaire — {questionnaireModal.quote_ref}</h2>
              <button onClick={() => setQuestionnaireModal(null)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>
              {quoteCompanyName(questionnaireModal, clients[questionnaireModal.client_id]) || '—'} · {questionnaireModal.audit_type}
            </div>
            <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: '12px 14px', maxHeight: '62vh', overflowY: 'auto' }}>
              <QuestionnaireView responses={questionnaireModal.form_responses} auditType={questionnaireModal.audit_type} />
            </div>
          </div>
        </div>
      )}
      {historyModal && <VersionHistoryModal quote={historyModal} versions={chainFor(historyModal)} onClose={() => setHistoryModal(null)} />}

      {sendOte && <SendOTEQuestionnaire onClose={() => setSendOte(false)} />}
      <div className="page-header"><h1>Quotations</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {service === 'ote' && <button className="primary" onClick={() => setSendOte(true)}>Send OTE questionnaire</button>}
          <ServiceSwitch ote value={service} onChange={setService} />
        </div>
      </div>
      {service === 'ote' && oteLinks && oteLinks.length > 0 && (
        <details className="card" style={{ marginBottom: 14, padding: '10px 14px' }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Awaiting questionnaire ({oteLinks.length}) — sent, not filled in yet</summary>
          <div style={{ marginTop: 8 }}>
            {oteLinks.map(l => {
              const days = Math.max(0, Math.floor((Date.now() - new Date(l.created_at).getTime()) / 86400000))
              const url = `${window.location.origin}/ote-form/${l.token}`
              return (
                <div key={l.token} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '5px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, minWidth: 200 }}>{l.clients?.name || 'New company'}</span>
                  <span style={{ color: 'var(--text2)' }}>{l.sent_to || '—'}</span>
                  <span style={{ color: 'var(--text2)' }}>sent {days}d ago</span>
                  <button className="sm" onClick={() => { navigator.clipboard.writeText(url); toast('Link copied', 'success', { detail: 'Paste it into a reminder email' }) }}>Copy link</button>
                </div>)
            })}
          </div>
        </details>)}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Search client or ref…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
        {[['to_prepare','To prepare'], ['awaiting','Awaiting signature'], ['history','History / archive'], ['all','All']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ fontWeight: filter === k ? 500 : 400, background: filter === k ? 'var(--text)' : 'var(--bg)', color: filter === k ? 'var(--bg)' : 'var(--text)' }}>
            {l}{counts[k] > 0 ? ` (${counts[k]})` : ''}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ref</th><th>Client</th><th>Audit type</th><th>Man-days</th>
                <th>Fee (ex GST)</th><th>Prepared by</th><th>Status</th><th>Signed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>
                  No quotations in this view.
                </td></tr>
              )}
              {rows.map(q => {
                const c = clients[q.client_id] || q.clients
                const s = effectiveStatus(q)
                return (
                  <tr key={q.id}>
                    <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 13 }}>{q.quote_ref}</td>
                    <td>{quoteCompanyName(q, c) || '—'}
                      {(q.form_responses?.remarks || '').trim() && (
                        <span className="badge badge-warn" style={{ marginLeft: 6, fontSize: 13 }} title={q.form_responses.remarks}>Client remark</span>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>{q.audit_type}</td>
                    <td style={{ fontSize: 13 }}>{mdLabel(q.mandays)}</td>
                    <td>{fmt(q.fee_ex_gst)}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{q.prepared_by || '—'}</td>
                    <td>{statusBadge(q)}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{q.signed_date ? format(parseISO(q.signed_date), 'd MMM yy') : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {/* Disabled for the split second the light list shows — every action needs the full quotation. */}
                      <fieldset disabled={!!q._lite} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {/* Questionnaire — read-only client responses, available at EVERY status */}
                        <button className="sm" onClick={() => setQuestionnaireModal(q)}>Questionnaire</button>
                        <button className="sm" onClick={() => setPreviewModal(q)}>Preview</button>
                        {/* Revised quotes: view the previous version(s) + revision reason (works for any status,
                            incl. a Sent r1 where Prepare/Edit isn't available). */}
                        {chainFor(q).length > 0 && <button className="sm" onClick={() => setHistoryModal(q)}>Versions ({chainFor(q).length + 1})</button>}
                        {/* Draft → prepare the quote in place. Sent quotes change ONLY via Revise (new version,
                            supersedes) so the audit trail of what the client was sent is preserved. */}
                        {s === 'Draft' && <button className="sm" onClick={() => setEditModal(q)}>Prepare</button>}
                        {/* Resend removed — the sign link is available from Preview (Copy link) and chasers are automated. */}
                        {/* Revise creates a new active version and supersedes this one (not at Draft — just edit it).
                            Archived enquiries show Restore instead (they were never sent; no version to revise). */}
                        {s !== 'Draft' && s !== 'Archived' && <button className="sm" onClick={() => reviseQuote(q)}>Revise</button>}
                        {s === 'Archived' && <button className="sm" onClick={() => restoreQuote(q)}>Restore</button>}
                        {/* Mutually-exclusive END STATES for a Sent quote consolidated into one Outcome menu
                            (Mark signed / Declined / Withdrawn) — same actions/logic, less button clutter. */}
                        {s === 'Sent' && (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button className="sm" onClick={(e) => {
                              if (outcomeMenu === q.id) { setOutcomeMenu(null); return }
                              const r = e.currentTarget.getBoundingClientRect()
                              const up = r.bottom + 150 > window.innerHeight          // not enough room below → flip up
                              setOutcomePos({ top: up ? r.top : r.bottom, left: r.right, up })
                              setOutcomeMenu(q.id)
                            }}>Outcome ▾</button>
                            {outcomeMenu === q.id && (<>
                              <div onClick={() => setOutcomeMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                              <div style={{ position: 'fixed', top: outcomePos?.top || 0, left: outcomePos?.left || 0, transform: outcomePos?.up ? 'translate(-100%, -100%)' : 'translateX(-100%)', marginTop: outcomePos?.up ? -4 : 4, background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', zIndex: 41, minWidth: 140, overflow: 'hidden' }}>
                                <button onClick={() => { setOutcomeMenu(null); markSigned(q) }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#27500a' }}>Mark signed</button>
                                <button onClick={() => { setOutcomeMenu(null); setQuoteStatus(q, 'Declined', 'Declined / Lost') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', borderTop: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>Declined</button>
                                <button onClick={() => { setOutcomeMenu(null); setQuoteStatus(q, 'Withdrawn', 'Withdrawn') }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', borderTop: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>Withdrawn</button>
                              </div>
                            </>)}
                          </div>
                        )}
                        {(s === 'Signed' || s === 'Cancelled') && q.job_id && <button className="sm" onClick={() => navigate(`/jobs/${q.job_id}`)}>View job →</button>}
                        {q.status === 'Draft' && <button className="sm" onClick={() => archiveQuote(q)}>Archive</button>}
                        {q.status === 'Draft' && <button className="sm" onClick={() => deleteQuote(q)} style={{ color: 'var(--danger)' }}>Delete</button>}
                      </div>
                      </fieldset>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}